import { z } from 'zod/v3';
import { makeFieldSchemaFromZod } from '../utils';

/**
 * Definition of a single input inside a remote auth option
 * (mirrors the RadioInputField field-definition contract).
 */
const RemoteFieldDefinitionSchema = z.object({
  name: z.string(),
  label: z.string(),

  type: z.enum(['text', 'password', 'select', 'string', 'custom']),

  options: z.array(z.string()).optional(),
  default: z.string().optional(),
  disabled: z.boolean().optional(),
  placeholder: z.string().optional(),

  component: z.string().optional(),
  componentProps: z.any().optional(),
});

/**
 * A selectable remote auth option (e.g. API Key / OAuth Client Credentials)
 * with its fields and optional RadioCheckboxCardField-specific uiOptions.
 */
const RemoteOptionSchema = z.object({
  label: z.string(),
  value: z.string(),
  fields: z.array(RemoteFieldDefinitionSchema).optional(),
  uiOptions: z
    .object({
      infoText: z
        .string()
        .optional()
        .describe('Info alert text (RadioCheckboxCardField variant only).'),
      checkboxLabel: z
        .string()
        .optional()
        .describe(
          'Label of the replace-secret checkbox (RadioCheckboxCardField variant only).',
        ),
    })
    .optional(),
});

/** Put all uiSchema options here, in the template they go under ui:options: */
export const ServerTypeRadioFieldUiOptionsSchema = z.object({
  remoteAuthVariant: z
    .enum(['radio', 'checkboxCard'])
    .optional()
    .describe(
      'Auth card variant for remote servers: "radio" (default) embeds RadioInputField, "checkboxCard" embeds RadioCheckboxCardField with replace-secret checkboxes (edit flow).',
    ),
  lockSelection: z
    .boolean()
    .optional()
    .describe(
      'Edit flow: locks the server-type radios and server URL input while keeping inner fields editable. Intended for use with remoteAuthVariant "checkboxCard" and prefilled formData.',
    ),
  hideAuthorizationWhen: z
    .object({
      field: z
        .string()
        .describe('Name of another form field to check, e.g. serverType.'),
      equals: z
        .string()
        .describe('Value that hides the authorization card, e.g. "Public".'),
    })
    .optional()
    .describe(
      'Hides the authorization card when the given form field equals the given value. The field is read from the accumulated form data, so it works across wizard steps. Defaults to { field: "serverType", equals: "Public" }.',
    ),
  disableRemoteWhen: z
    .object({
      field: z.string(),
      equals: z.string(),
    })
    .optional()
    .describe(
      'Disables the remote deployment option when another form field matches.',
    ),
  remoteLabel: z
    .string()
    .optional()
    .describe(
      'Label of the remote server radio button. Defaults to "Remote MCP Server".',
    ),
  localLabel: z
    .string()
    .optional()
    .describe(
      'Label of the local server radio button. Defaults to "Local MCP Server".',
    ),
  remoteOptions: z
    .array(RemoteOptionSchema)
    .describe(
      'Auth options (labels + field definitions) shown for a remote server, e.g. API Key / OAuth Client Credentials.',
    ),
  serverUrlLabel: z
    .string()
    .optional()
    .describe('Label of the server URL input. Defaults to "MCP Server URL".'),
  serverUrlPlaceholder: z
    .string()
    .optional()
    .describe('Placeholder text for the server URL input.'),
  remoteCardTitle: z
    .string()
    .optional()
    .describe('Title of the remote authorization card. No title by default.'),
  remoteCardDescription: z
    .string()
    .optional()
    .describe('Description of the remote authorization card.'),
  localCardTitle: z
    .string()
    .optional()
    .describe('Title of the local configuration card.'),
  localCardDescription: z
    .string()
    .optional()
    .describe('Description of the local configuration card.'),
});

/** Transforms the Zod schema to a TypeScript type */
export type ServerTypeRadioFieldUiOptions = z.infer<
  typeof ServerTypeRadioFieldUiOptionsSchema
>;

const ConfigurationSchema = z.object({
  type: z.string(),
  jsonFile: z.string(),
});

/**
 * The return value schema: remote servers emit serverUrl + authorization,
 * local servers emit one or more typed configurations.
 */
export const ServerTypeRadioFieldValueSchema = z.object({
  serverDeploymentModel: z.enum(['remote', 'local']),
  serverUrl: z.string().optional(),
  authorization: z.record(z.string(), z.any()).optional(),
  configurations: z.array(ConfigurationSchema).optional(),
});

/** The value emitted by the ServerTypeRadioField */
export type ServerTypeRadioFieldValue = z.infer<
  typeof ServerTypeRadioFieldValueSchema
>;

/** The field schema for the ServerTypeRadioField field extension */
export const ServerTypeRadioFieldFieldSchema = makeFieldSchemaFromZod(
  ServerTypeRadioFieldValueSchema.describe(
    'MCP server type selection with remote authorization or local configurations.',
  ),
  ServerTypeRadioFieldUiOptionsSchema,
);

/** Transforms the field schema props to a TypeScript type */
export type ServerTypeRadioFieldProps =
  typeof ServerTypeRadioFieldFieldSchema.type;

/** The final schema used for the field extension */
export const ServerTypeRadioFieldSchema =
  ServerTypeRadioFieldFieldSchema.schema;
